import { createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from 'store';
import { getUrlWithParams, urls } from 'store/api';
import { apiClient } from 'utils/http';
import { uploadContent, uploadImage, uploadImageArray } from 'utils/upload';

import {
  IAttractionDraftItem,
  IAttractionItem,
  IAttractionItemResponse,
} from './interfaces';

type TListResponse = { data: IAttractionItemResponse[]; total: number };

export const loadAttractionsList = createAsyncThunk<
  TListResponse,
  {
    name?: string;
    limit?: number;
    offset?: number;
    entityType?: string;
    type?: string;
    sort?: string;
    lang?: string;
    isSortPriority?: boolean;
  },
  { state: RootState }
>('attractions/loadList', async (params, { getState }) => {
  params.limit = params.limit || getState().attractions.limit;
  const { data } = await apiClient.get<TListResponse>(
    urls.api.attractions.get,
    {
      params: params,
    }
  );
  return data;
});

export const loadAttractionItem = createAsyncThunk<
  IAttractionItemResponse,
  string
>('attraction/loadItem', async id => {
  const { data } = await apiClient.get<IAttractionItemResponse>(
    getUrlWithParams(urls.api.attractions.getOne, {
      id,
    })
  );
  return data;
});

export const bulkUpdateAttractionsItem = createAsyncThunk<
  IAttractionItem[],
  Partial<IAttractionItem>[]
>('attraction/bulkUpdateItems', async params => {
  const { data } = await apiClient.patch<IAttractionItem[]>(
    urls.api.attractions.bulkPatch,
    params
  );
  return data;
});

export const saveAttractionItem = createAsyncThunk<
  IAttractionItem,
  Partial<IAttractionItem>
>('attraction/saveItem', async ({ id, ...params }) => {
  const image = await uploadImage(params.image);
  const heroImage = await uploadImage(params.heroImage);
  const gallery = params.gallery && (await uploadImageArray(params.gallery));
  const content = params.content && (await uploadContent(params.content));

  if (id) {
    const { data } = await apiClient.patch<IAttractionItem>(
      getUrlWithParams(urls.api.attractions.patch, { id }),
      {
        ...params,
        image,
        heroImage,
        gallery,
        content,
      }
    );
    return data;
  } else {
    const { data } = await apiClient.post<IAttractionItem>(
      urls.api.attractions.post,
      {
        ...params,
        image,
        heroImage,
        gallery,
        content,
      }
    );
    return data;
  }
});

export const saveAttractionItemAsDraft = createAsyncThunk<
  IAttractionDraftItem,
  IAttractionDraftItem
>('attraction/saveItem', async params => {
  const { id, ...requestParams } = params;
  const image = await uploadImage(requestParams.image);
  const heroImage = await uploadImage(requestParams.heroImage);
  const gallery = await uploadImageArray(requestParams.gallery);
  const content = await uploadContent(requestParams.content);
  if (id) {
    const { data } = await apiClient.patch<IAttractionDraftItem>(
      getUrlWithParams(urls.api.attractions.draft.patch, { id }),
      {
        ...requestParams,
        image,
        heroImage,
        gallery,
        content,
      }
    );
    return data;
  } else {
    const { data } = await apiClient.post<IAttractionDraftItem>(
      urls.api.attractions.draft.post,
      {
        ...requestParams,
        image,
        heroImage,
        gallery,
        content,
      }
    );
    return data;
  }
});

export const setPublishAttraction = createAsyncThunk<void, string>(
  'attractions/publish',
  async id => {
    return await apiClient.patch(
      getUrlWithParams(urls.api.attractions.publish.patch, { id })
    );
  }
);

export const setUnPublishAttraction = createAsyncThunk<void, string>(
  'attractions/unPublish',
  async id => {
    return await apiClient.patch(
      getUrlWithParams(urls.api.attractions.unPublish.patch, { id })
    );
  }
);

export const removeAttractionItem = createAsyncThunk<
  {
    accessToken: string;
    refreshToken: string;
  },
  {
    id: number;
  }
>('attraction/removeItem', async params => {
  const { data } = await apiClient.delete<{
    accessToken: string;
    refreshToken: string;
  }>(getUrlWithParams(urls.api.attractions.delete, { id: params.id }));
  return data;
});
